import type { PoiType } from "./pois.ts";
import type { BiomeName } from "./resources.ts";

/**
 * Enfeites do chão (F44, doc 08): pedrinhas, pedras soltas, lajes, mudas, troncos caídos e tábuas na
 * praia. Só visuais: sem colisor, sem servidor e fora de qualquer hash. O cliente os espalha por chunk
 * no worker de terreno, pelo padrão dos recursos (`deriveSeed(seed, "detalhes", índice)` e um hash sem
 * estado por célula), então o resultado não depende da ordem dos chunks nem do worker.
 */
export interface DetailPieceInfo {
  /** Medidas na escala 1 (m): largura (x), altura e comprimento (z), base em y = 0 e centro em xz = 0. */
  readonly size: readonly [number, number, number];
  /** Peça comprida, deitada ao longo de z (tronco, tábua): assenta pelo eixo inteiro. */
  readonly long?: boolean;
  /** Peça larga e chata (laje): assenta pela área, não só pelo centro. */
  readonly flat?: boolean;
  /**
   * Alcance da malha no chão (m, escala 1), medido no modelo e na reserva procedural: nas peças
   * redondas, a partir da origem (o pé da muda não é o centro da copa); nas compridas, a meia largura em
   * volta do eixo z (o toco do tronco sai para um lado). As exclusões do espalhamento usam este, não a
   * caixa.
   */
  readonly radius: number;
}

/**
 * Peças dos enfeites, na ordem estável em que o worker as agrupa (uma `InstancedMesh` cada no cliente).
 * As que têm modelo no pipeline são ajustadas a estas medidas (`pedra-solta` é a mesma peça dos POIs);
 * sem modelo, a malha procedural de reserva tem as mesmas medidas. O tronco caído só existe procedural.
 */
export const DETAIL_PIECES = {
  /** Pedrinha (Models-2), 40 cm na maior medida. */
  pedrinha: { size: [0.31, 0.22, 0.4], radius: 0.23 },
  /** A pedra solta do pátio da Caverna (bundle `pois`). */
  "pedra-solta": { size: [0.82, 0.45, 0.69], radius: 0.54 },
  /** Laje de pedra rasa (Models-2), que acompanha o declive. */
  "laje-pedra": { size: [1.2, 0.35, 1.18], flat: true, radius: 0.66 },
  /** Mudas (Modelsa): 1,2 m na escala 1, finas, sem colisor. */
  "muda-conifera": { size: [0.55, 1.2, 0.6], radius: 0.35 },
  "muda-folhosa": { size: [0.83, 1.2, 0.64], radius: 0.54 },
  /** Tronco caído com um toco de galho para +x (procedural): 2,6 m e raio 0,18 m na escala 1, eixo em x = 0. */
  "tronco-caido": { size: [0.65, 0.36, 2.6], long: true, radius: 0.49 },
  /** Tábua solta (ResourceBits), 1,4 m. */
  tabua: { size: [0.37, 0.14, 1.4], long: true, radius: 0.2 },
} as const satisfies Record<string, DetailPieceInfo>;

export type DetailPiece = keyof typeof DETAIL_PIECES;

/** Ordem estável das peças (índice do grupo no worker e da malha no cliente). */
export const DETAIL_PIECE_IDS = Object.keys(DETAIL_PIECES) as DetailPiece[];

export type GroundDetailId = "pedrinha" | "pedra-solta" | "laje" | "muda" | "tronco" | "tabua";

/** Como a peça assenta: inclinada com o chão, em pé (mudas) ou deitada ao longo da curva de nível. */
export type DetailPose = "chao" | "em-pe" | "nivel";

export interface GroundDetailRule {
  /** Índice estável: entra na seed (`deriveSeed(seed, "detalhes", index)`). Acrescente sempre no fim. */
  index: number;
  id: GroundDetailId;
  /** Peça de cada instância; com `mix`, a segunda sai com a chance do bioma (as outras usam a primeira). */
  pieces: readonly [DetailPiece] | readonly [DetailPiece, DetailPiece];
  mix?: Partial<Record<BiomeName, number>>;
  /** Lado da célula da grade de candidatos (m; divide 32): no máximo um candidato, ou um grupo, por célula. */
  cell: number;
  /** Peças por 100 m² de chão elegível, por bioma (a média; as manchas de 16 m variam em volta dela). */
  density: Partial<Record<BiomeName, number>>;
  /** Declive máximo (tangente). */
  maxSlope: number;
  /** Altura mínima do chão (m): nada na água nem na espuma. `minYBy` sobe por bioma. */
  minY: number;
  minYBy?: Partial<Record<BiomeName, number>>;
  pose: DetailPose;
  /** Faixa de escala uniforme (no tronco, só da seção). */
  scale: readonly [number, number];
  /** Comprimento (m) por instância das peças compridas (escala só em z). */
  length?: readonly [number, number];
  /** Fração da altura da peça enterrada no chão. */
  sink: number;
  /** Peças por célula sorteada (tábuas espalhadas juntas), uniforme na faixa. */
  group?: readonly [number, number];
  /** Só na praia, a menos de `coast` m do mar e a até `radius` m de um destes POIs. */
  shore?: { coast: number; near: readonly PoiType[]; radius: number };
}

/** Manchas: o peso de cada tipo varia numa grade de 16 m, entre 0,3 e 1,7 vezes a média (média 1). */
export const DETAIL_PATCH = { cell: 16, min: 0.3, max: 1.7 } as const;

/** Nenhum enfeite abaixo desta altura (m): a água e a espuma da beira. */
export const DETAIL_MIN_Y = 0.15;

/**
 * Regras por bioma (decisões do dono, 2026-10-05). Os biomas do jogador são os internos: Floresta =
 * `mata`, Charneca = `cerrado`, Costa = `praia`, Pântano = `mangue` e Montanhas = `serra`. As tábuas
 * são do naufrágio: só na praia, perto do mar e a até 120 m dos Destroços ou da Costa de chegada.
 */
export const GROUND_DETAILS: readonly GroundDetailRule[] = [
  { index: 0, id: "pedrinha", pieces: ["pedrinha"], cell: 2, density: { mata: 3, cerrado: 4, praia: 2, serra: 6 }, maxSlope: 0.84, minY: DETAIL_MIN_Y, pose: "chao", scale: [0.55, 1.15], sink: 0.3 },
  { index: 1, id: "pedra-solta", pieces: ["pedra-solta"], cell: 4, density: { mata: 0.5, cerrado: 1, praia: 0.3, serra: 2 }, maxSlope: 0.7, minY: DETAIL_MIN_Y, pose: "chao", scale: [0.6, 1], sink: 0.2 },
  { index: 2, id: "laje", pieces: ["laje-pedra"], cell: 8, density: { mata: 0.15, cerrado: 0.4, serra: 0.8 }, maxSlope: 0.47, minY: DETAIL_MIN_Y, pose: "chao", scale: [0.7, 1.15], sink: 0.35 },
  {
    index: 3,
    id: "muda",
    pieces: ["muda-folhosa", "muda-conifera"],
    // Coníferas nas montanhas e na costa (onde estão os pinheiros), folhosas na floresta e no pântano.
    mix: { serra: 0.9, praia: 0.7, mata: 0.3, cerrado: 0.15 },
    cell: 8,
    density: { mata: 0.6, cerrado: 0.25, praia: 0.1, mangue: 0.3, serra: 0.15 },
    maxSlope: 0.58,
    minY: DETAIL_MIN_Y,
    minYBy: { mangue: 0.25 },
    pose: "em-pe",
    scale: [0.6, 1],
    sink: 0.04,
  },
  { index: 4, id: "tronco", pieces: ["tronco-caido"], cell: 16, density: { mata: 0.12, cerrado: 0.04, praia: 0.03, mangue: 0.15 }, maxSlope: 0.27, minY: DETAIL_MIN_Y, pose: "nivel", scale: [0.83, 1.22], length: [2.2, 3.2], sink: 0.3 },
  {
    index: 5,
    id: "tabua",
    pieces: ["tabua"],
    cell: 8,
    density: { praia: 0.4 },
    maxSlope: 0.27,
    minY: DETAIL_MIN_Y,
    pose: "chao",
    scale: [0.8, 1.15],
    sink: 0.3,
    group: [1, 3],
    shore: { coast: 30, near: ["destrocos", "chegada"], radius: 120 },
  },
];

export const GROUND_DETAIL_BY_ID = new Map(GROUND_DETAILS.map((r) => [r.id, r]));

/** Peças por grupo, em média (1 sem grupo). */
export const groupMean = (rule: Pick<GroundDetailRule, "group">): number => (rule.group ? (rule.group[0] + rule.group[1]) / 2 : 1);

/**
 * Chance de uma célula ter um candidato no bioma, sem a mancha: peças por m² vezes a área da célula,
 * dividida pelas peças de cada grupo. Com a mancha mais forte (1,7), cabe em 1 (testado).
 */
export const cellChance = (rule: GroundDetailRule, biome: BiomeName): number => ((rule.density[biome] ?? 0) * rule.cell * rule.cell) / 100 / groupMean(rule);
